import { useState } from 'react';

/**
 * Resource opens per day, from Module 10's access log.
 *
 * <p>A single series, so there is no legend — the panel heading already says what is
 * plotted. Line at 2px over an area wash, one baseline, and a hover layer, because an
 * SVG chart on a page is interactive whether or not it was designed to be.
 */
const HEIGHT = 132;
const WIDTH = 640;
const PAD = { top: 12, right: 8, bottom: 12, left: 8 };

export default function ActivitySparkline({ days }) {
  const [hover, setHover] = useState(null);

  if (!days || days.length === 0) return null;

  const max = Math.max(1, ...days.map((day) => day.opens));
  const plotWidth = WIDTH - PAD.left - PAD.right;
  const plotHeight = HEIGHT - PAD.top - PAD.bottom;
  const step = plotWidth / days.length;

  const x = (index) =>
    PAD.left + (days.length === 1 ? plotWidth / 2 : (index / (days.length - 1)) * plotWidth);
  const y = (value) => PAD.top + plotHeight - (value / max) * plotHeight;

  const line = days
    .map((day, index) => `${index === 0 ? 'M' : 'L'}${x(index)},${y(day.opens)}`)
    .join(' ');
  const area = `${line} L${x(days.length - 1)},${PAD.top + plotHeight} L${x(0)},${PAD.top + plotHeight} Z`;
  const last = days[days.length - 1];
  const shown = hover === null ? days.length - 1 : hover;

  return (
    <div style={{ position: 'relative' }}>
      <svg
        className="spark"
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        preserveAspectRatio="none"
        role="img"
        aria-label={`Resource opens per day over the last ${days.length} days, ending at ${last.opens}`}
        onMouseLeave={() => setHover(null)}
      >
        {/* One baseline, hairline and recessive. */}
        <line
          x1={PAD.left}
          y1={PAD.top + plotHeight}
          x2={WIDTH - PAD.right}
          y2={PAD.top + plotHeight}
          stroke="var(--chart-grid)"
          strokeWidth="1"
        />
        <path d={area} fill="var(--chart-2)" fillOpacity="0.1" />
        <path
          d={line}
          fill="none"
          stroke="var(--chart-2)"
          strokeWidth="2"
          strokeLinejoin="round"
          strokeLinecap="round"
        />

        {/* The end point is the one worth marking, with a surface ring so it stays
            legible where it sits on the line. */}
        <circle
          cx={x(days.length - 1)}
          cy={y(last.opens)}
          r="4"
          fill="var(--chart-2)"
          stroke="var(--color-surface)"
          strokeWidth="2"
        />
        {hover !== null && (
          <circle
            cx={x(hover)}
            cy={y(days[hover].opens)}
            r="4"
            fill="var(--chart-3)"
            stroke="var(--color-surface)"
            strokeWidth="2"
          />
        )}

        {/* Hit targets far wider than the marks, so hovering does not require aim. */}
        {days.map((day, index) => (
          <rect
            key={day.date}
            x={x(index) - step / 2}
            y="0"
            width={step}
            height={HEIGHT}
            fill="transparent"
            onMouseEnter={() => setHover(index)}
          />
        ))}
      </svg>

      <div className="row-between" style={{ marginTop: 4 }}>
        <span className="tiny muted">{formatDay(days[0].date)}</span>
        <span className="tiny muted">
          <b>{days[shown].opens.toLocaleString()}</b> on {formatDay(days[shown].date)}
        </span>
        <span className="tiny muted">{formatDay(last.date)}</span>
      </div>
    </div>
  );
}

function formatDay(iso) {
  return new Date(`${iso}T00:00:00Z`).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  });
}
